import type { UseListProps, UseListResult } from '@ztechapps/common.hook'
import { StorageType, useList, useUserTimeZone } from '@ztechapps/common.hook'
import { useMemo } from 'react'
import type { Product } from '@ztechapps/common.contract'
import merge from 'lodash/merge'
import omit from 'lodash/omit'
import { productListQuery } from '../graphql'

export type UseProductListProps = Partial<UseListProps>

export const useProductList = ({ query, ...rest }: UseProductListProps): UseListResult<Product> => {
  const timeZone = useUserTimeZone()

  const mergeUseListProps = useMemo(() => {
    //we cannot merge directly the query because lodash will do the infinite loop
    //so we remove it first and assign it later it later

    const defaultProps: UseListProps = {
      query: productListQuery,
      entity: 'productList',
      sort: { code: -1 },
      limit: 100,
      options: {
        queryOptions: {
          fetchPolicy: 'cache-and-network', //make it request for network for the list
        },
      },
      listActionOptions: {
        storageType: StorageType.localStorage,
        timeZone,
      },
    }

    const restDefaultProps = omit(defaultProps, ['query'])

    //the rest here
    let finalProps = merge({}, restDefaultProps, rest) as UseListProps
    finalProps = {
      ...finalProps,
      query: query ?? defaultProps?.query,
    }

    return finalProps
  }, [rest, query, timeZone])

  return usePublicProductList(mergeUseListProps)
}

/**
 * Export the the product list without the dependency on login user
 * Config here is also not store on storage
 * @param query
 * @param rest
 */
export const usePublicProductList = ({ query, ...rest }: UseProductListProps): UseListResult<Product> => {
  const mergeUseListProps = useMemo(() => {
    //we cannot merge directly the query because lodash will do the infinite loop
    //so we remove it first and assign it later it later

    const defaultProps: UseListProps = {
      query: productListQuery,
      entity: 'productList',
      sort: { code: -1 },
      limit: 100,
      options: {
        queryOptions: {
          fetchPolicy: 'cache-and-network', //make it request for network for the list
        },
      },
      listActionOptions: {
        storageType: StorageType.none,
      },
    }

    const restDefaultProps = omit(defaultProps, ['query'])

    //the rest here
    let finalProps = merge({}, restDefaultProps, rest) as UseListProps
    finalProps = {
      ...finalProps,
      query: query ?? defaultProps?.query,
    }

    return finalProps
  }, [rest, query])

  return useList<Product>(mergeUseListProps)
}
